import { ConfigProvider, theme as antdTheme } from 'antd'
import type { ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { RequirePlatformAdmin } from '../guards'
import { useEffectiveTheme } from '../ui/themePreference'
import AIChatConversationsPage from './AIChatConversationsPage'
import AIChatTasksPage from './AIChatTasksPage'
import PlatformDomainsPage from './DomainsPage'
import ExternalServicesPage from './ExternalServicesPage'
import MarketingAnalyticsPage from './MarketingAnalyticsPage'
import ProviderCredentialsPage from './ProviderCredentialsPage'
import ProviderRegistryPage from './ProviderRegistryPage'
import PlatformOverviewPage from './OverviewPage'
import PlatformLayout from './PlatformLayout'
import AIBillingRulesPage from './AIBillingRulesPage'
import PlanCreditsBillingPage from './PlanCreditsBillingPage'
import PlanAuthMatrixPage from './PlanAuthMatrixPage'
import AIPricingPage from './AIPricingPage'
import RateRulesPage from './RateRulesPage'
import PlatformSecurityPage from './SecurityPage'
import SystemSettingsPage from './SystemSettingsPage'
import SkillDetailPage from './SkillDetailPage'
import SkillLibraryPage from './SkillLibraryPage'
import PlatformSitesPage from './SitesPage'
import StorageOperationsPage from './StorageOperationsPage'
import PlatformTenantDetailPage from './TenantDetailPage'
import TenantEntitlementMatrixPage from './TenantEntitlementMatrixPage'
import PlatformTenantsPage from './TenantsPage'
import PlatformWorkersPage from './WorkersPage'
import EngineProfilesPage from './EngineProfilesPage'
import AgentRuntimesPage from './AgentRuntimesPage'
import ProvidersPage from './ProvidersPage'
import ModelsPage from './ModelsPage'
import ModelCapabilitiesPage from './ModelCapabilitiesPage'
import './platform-shell.css'
import './platform-tables.css'

function PlatformThemeProvider({ children }: { children: ReactNode }) {
  const effective = useEffectiveTheme()
  const dark = effective === 'dark'
  return (
    <ConfigProvider
      theme={{
        algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#5B5FF5',
          borderRadius: 6,
          fontFamily:
            'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif',
          ...(dark
            ? {
                colorBgContainer: '#111827',
                colorBgElevated: '#172033',
                colorBgLayout: '#0b1120',
                colorBorder: '#243044',
                colorBorderSecondary: '#1e2a3c',
                colorText: '#f8fafc',
                colorTextSecondary: '#cbd5e1',
                colorTextTertiary: '#94a3b8',
                colorTextQuaternary: '#7c8aa0',
                colorTextPlaceholder: '#7c8aa0',
                colorTextDisabled: '#5b6b84',
              }
            : {}),
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}

/** Mounted at `/platform/*` — spec §22/§31: requires `platform_admin`,
 * a distinct scope from Workspace membership (see `../workspace/WorkspaceApp`). */
export default function PlatformApp() {
  return (
    <RequirePlatformAdmin>
      <PlatformThemeProvider>
        <Routes>
          <Route element={<PlatformLayout />}>
            {/* VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_
                FOUNDATION_V1 (task spec §4/§7/§89) — Marketing Analytics is
                now the Platform Admin default home; the bare `/platform`
                index canonically redirects here rather than rendering
                inline, so the URL bar always reflects the real page (no
                "hidden" index route a bookmark could silently diverge
                from). `overview` (task spec §5/§88) keeps its own stable
                route — no deep link/bookmark to the old default breaks. */}
            <Route index element={<Navigate to="marketing-analytics" replace />} />
            <Route path="marketing-analytics" element={<MarketingAnalyticsPage />} />
            <Route path="overview" element={<PlatformOverviewPage />} />
            <Route path="tenants" element={<PlatformTenantsPage />} />
            <Route path="tenants/:workspaceId" element={<PlatformTenantDetailPage />} />
            <Route path="tenant-plans" element={<TenantEntitlementMatrixPage />} />
            <Route path="plan-auth-matrix" element={<PlanAuthMatrixPage />} />
            <Route path="sites" element={<PlatformSitesPage />} />
            <Route path="domains" element={<PlatformDomainsPage />} />
            <Route path="workers" element={<PlatformWorkersPage />} />
            <Route path="engine-profiles" element={<EngineProfilesPage />} />
            <Route path="agent-runtimes" element={<AgentRuntimesPage />} />
            <Route path="ai-chat/tasks" element={<AIChatTasksPage />} />
            <Route path="ai-chat/conversations" element={<AIChatConversationsPage />} />
            <Route path="skills" element={<SkillLibraryPage />} />
            <Route path="skills/:skillId" element={<SkillDetailPage />} />
            <Route path="providers" element={<ProvidersPage />} />
            <Route path="models" element={<ModelsPage />} />
            <Route path="model-capabilities" element={<ModelCapabilitiesPage />} />
            <Route path="credential-providers" element={<ProviderRegistryPage />} />
            <Route path="provider-credentials" element={<ProviderCredentialsPage />} />
            <Route path="storage" element={<StorageOperationsPage />} />
            <Route path="rate-rules" element={<RateRulesPage />} />
            <Route path="plan-credits" element={<PlanCreditsBillingPage />} />
            <Route path="ai-billing-rules" element={<AIBillingRulesPage />} />
            <Route path="ai-cost-pricing" element={<AIPricingPage />} />
            <Route path="system-settings" element={<SystemSettingsPage />} />
            <Route path="security" element={<PlatformSecurityPage />} />
            <Route path="external-services" element={<ExternalServicesPage />} />
            <Route path="*" element={<Navigate to="marketing-analytics" replace />} />
          </Route>
        </Routes>
      </PlatformThemeProvider>
    </RequirePlatformAdmin>
  )
}
